import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bluetooth, Cable, CheckCircle2, Loader2, Network, Printer, RefreshCw, Smartphone } from 'lucide-react';
import { discoverWithAgent, getAgentHealth, setupQueueWithAgent } from '../lib/printTransports/agent';
import { isWebBluetoothSupported, pairBluetoothPrinter } from '../lib/printTransports/webBluetooth';

// Searches by itself for printers on this device (via the local print agent):
// network printers with their IP, USB printers (also when not yet set up in the OS),
// Bluetooth printers paired with the computer, plus BLE printers on phones (Web Bluetooth).
// onSelect receives ready-to-save printer values: {name, type, connection_string, paper_width}.

const GROUPS = [
  {
    id: 'network',
    label: 'Netzwerk',
    icon: Network,
    empty: 'Kein Netzwerkdrucker gefunden. Ist der Drucker eingeschaltet und per Kabel/WLAN im selben Netz wie dieses Gerät? '
      + 'Die IP steht auf der Selbsttest-Seite des Druckers. Liegt sie in einem anderen Netz (z. B. 192.168.123.x), muss sie am Drucker angepasst werden.',
  },
  { id: 'usb', label: 'USB', icon: Cable, empty: 'Kein USB-Drucker gefunden. Kabel und Stromversorgung prüfen.' },
  {
    id: 'bluetooth',
    label: 'Bluetooth',
    icon: Bluetooth,
    empty: 'Kein gekoppelter Bluetooth-Drucker. Drucker zuerst in den Bluetooth-Einstellungen des Computers koppeln.',
  },
  { id: 'other', label: 'Weitere Drucker', icon: Printer },
];

const BUTTON = 'inline-flex h-9 items-center justify-center border border-border bg-card px-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60';
const PRIMARY_BUTTON = 'inline-flex h-9 items-center justify-center bg-primary px-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60';

function candidateKey(candidate) {
  return `${candidate.connection}:${candidate.connection_string || candidate.uri}`;
}

function paperLabel(width) {
  return Number(width) >= 200 ? 'A4' : `${width} mm`;
}

function detailLine(candidate) {
  if (candidate.connection === 'network') {
    if (candidate.ip) return `IP ${candidate.ip}${candidate.port ? `:${candidate.port}` : ''}`;
    return 'Netzwerkdrucker (IP im Moment nicht erreichbar)';
  }
  if (candidate.needs_setup) return 'Angeschlossen, wird beim Übernehmen im System eingerichtet';
  if (candidate.type === 'serial') return `Anschluss ${candidate.connection_string}`;
  return `Systemdrucker ${candidate.connection_string}`;
}

export function candidateToPrinterValues(candidate) {
  return {
    name: candidate.name,
    type: candidate.type,
    connection_string: candidate.connection_string,
    paper_width: Number(candidate.paper_width) || 80,
  };
}

export default function PrinterDiscovery({ existingPrinters = [], onSelect, autoStart = true }) {
  const [phase, setPhase] = useState('idle');
  const [printers, setPrinters] = useState([]);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const search = useCallback(async () => {
    setError('');
    setPhase('checking');
    if (!(await getAgentHealth())) {
      setPhase('no-agent');
      return;
    }
    setPhase('searching');
    try {
      const result = await discoverWithAgent();
      setPrinters(result.printers || []);
      setPhase('done');
    } catch (searchError) {
      setError(searchError.message);
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    if (autoStart) search();
  }, [autoStart, search]);

  const configured = useMemo(() => {
    const targets = new Set();
    existingPrinters.forEach((printer) => {
      const target = String(printer.connection_string || '').trim();
      if (target) targets.add(target.toLowerCase());
      const ip = target.split(':')[0];
      if (/^\d{1,3}(\.\d{1,3}){3}$/.test(ip)) targets.add(ip);
    });
    return targets;
  }, [existingPrinters]);

  const isConfigured = (candidate) => (
    configured.has(String(candidate.connection_string || '').toLowerCase())
    || (candidate.ip && configured.has(candidate.ip))
  );

  const choose = async (candidate) => {
    if (!candidate.needs_setup) {
      onSelect(candidateToPrinterValues(candidate));
      return;
    }
    setBusyKey(candidateKey(candidate));
    setError('');
    try {
      const queue = await setupQueueWithAgent({ uri: candidate.uri, name: candidate.name });
      onSelect({ ...candidateToPrinterValues(candidate), type: 'system', connection_string: queue });
    } catch (setupError) {
      setError(setupError.message);
    } finally {
      setBusyKey('');
    }
  };

  const pairPhonePrinter = async () => {
    setError('');
    try {
      const paired = await pairBluetoothPrinter();
      onSelect({ name: paired.name, type: 'bluetooth', connection_string: paired.id, paper_width: 58 });
    } catch (pairError) {
      if (pairError?.name !== 'NotFoundError') setError(pairError.message); // NotFoundError = picker cancelled
    }
  };

  const searching = phase === 'checking' || phase === 'searching';
  const grouped = GROUPS.map((group) => ({
    ...group,
    items: printers.filter((candidate) => (candidate.connection || 'other') === group.id),
  })).filter((group) => group.items.length > 0 || (phase === 'done' && group.empty));

  return (
    <div className="space-y-4 border border-border bg-muted/40 p-4" data-testid="printer-discovery">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-foreground">Drucker automatisch suchen</p>
          <p className="text-sm text-muted-foreground">
            {searching
              ? 'Suche im Netzwerk (inkl. IP), an USB und Bluetooth …'
              : phase === 'done'
                ? `${printers.length} Drucker gefunden`
                : 'Netzwerk, USB und Bluetooth dieses Geräts'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isWebBluetoothSupported() ? (
            <button type="button" className={BUTTON} onClick={pairPhonePrinter}>
              <Smartphone className="mr-2 h-4 w-4" />
              Bluetooth in der Nähe
            </button>
          ) : null}
          <button type="button" className={BUTTON} onClick={search} disabled={searching}>
            {searching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Neu suchen
          </button>
        </div>
      </div>

      {error ? <p className="text-sm font-medium text-red-600">{error}</p> : null}

      {phase === 'no-agent' ? (
        <div className="space-y-2 text-sm text-muted-foreground">
          <p className="font-semibold text-amber-700">Auf diesem Gerät läuft kein Druck-Agent.</p>
          <p>
            Der Agent findet Netzwerk-, USB- und Bluetooth-Drucker automatisch. Starten mit{' '}
            <span className="font-mono">python3 print-agent/fiscalpos_print_agent.py</span>
            {' '}(macOS-Autostart: <span className="font-mono">print-agent/install_macos.sh</span>), dann „Neu suchen“.
          </p>
          {isWebBluetoothSupported() ? (
            <p>Auf dem Handy ohne Agent: „Bluetooth in der Nähe“ tippen und den mobilen Drucker wählen.</p>
          ) : null}
        </div>
      ) : null}

      {grouped.map((group) => {
        const Icon = group.icon;
        return (
          <div key={group.id} className="space-y-2">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Icon className="h-4 w-4" />
              {group.label}
            </p>
            {group.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{group.empty}</p>
            ) : (
              group.items.map((candidate) => {
                const key = candidateKey(candidate);
                const alreadyConfigured = isConfigured(candidate);
                return (
                  <div key={key} className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card px-3 py-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground">{candidate.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {detailLine(candidate)} · {paperLabel(candidate.paper_width)}
                        {candidate.status === 'offline' ? <span className="ml-2 font-semibold text-amber-700">Offline</span> : null}
                      </p>
                    </div>
                    {alreadyConfigured ? (
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-lime-700">
                        <CheckCircle2 className="h-4 w-4" />
                        Bereits eingerichtet
                      </span>
                    ) : (
                      <button type="button" className={PRIMARY_BUTTON} onClick={() => choose(candidate)} disabled={busyKey === key}>
                        {busyKey === key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {candidate.needs_setup ? 'Einrichten & übernehmen' : 'Übernehmen'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
}
